/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Image Cards Section - 图片卡片展示区块（支持深色/浅色主题）
 */
import Image from 'next/image';
import React from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';

import styles from './index.module.scss';

export interface ImageCardItem {
  /** 卡片唯一 ID */
  id: string;
  /** 卡片标题 */
  title: string;
  /** 卡片描述 */
  description: string;
  /** 展示图片 URL */
  imageUrl: string;
  /** 图片 alt 文本 */
  imageAlt: string;
}

export interface ImageCardsSectionProps {
  /** 区块标题 */
  title: string;
  /** 高亮标题（第二行） */
  highlightTitle?: string;
  /** 区块副标题 */
  subtitle: string;
  /** 功能卡片列表 */
  cards: ImageCardItem[];
  /** CTA 按钮文本 */
  ctaText: string;
  /** CTA 跳转链接 */
  ctaUrl?: string;
  /** 主题：dark 深色背景，light 浅色背景 */
  theme?: 'dark' | 'light';
}

const ImageCardsSection: React.FC<ImageCardsSectionProps> = ({
  title,
  highlightTitle,
  subtitle,
  cards,
  ctaText,
  ctaUrl = '/ai-video-generator',
  theme = 'light',
}) => {
  const isDark = theme === 'dark';

  return (
    <section className={`${styles.section} ${isDark ? styles.dark : styles.light}`}>
      <div className={styles.container}>
        {/* 标题区域 */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {title}
            {highlightTitle && (
              <>
                <br />
                <span className={styles.highlightTitle}>{highlightTitle}</span>
              </>
            )}
          </h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {/* 卡片网格 */}
        <div className={styles.cardsGrid}>
          {cards.map((card) => (
            <div key={card.id} className={styles.card}>
              {/* 卡片图片 */}
              <div className={styles.cardImageWrapper}>
                <Image
                  src={card.imageUrl}
                  alt={card.imageAlt}
                  fill
                  className={styles.cardImage}
                  sizes="(max-width: 768px) 100vw, 560px"
                />
              </div>

              {/* 卡片内容 */}
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <p className={styles.cardDescription}>{card.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA 按钮 */}
        <div className={styles.ctaWrapper}>
          <StartButton text={ctaText} href={ctaUrl} className={styles.ctaButton} />
        </div>
      </div>
    </section>
  );
};

export default ImageCardsSection;
